import { z } from 'zod';

export const WarrantyOriginSchema = z.enum(['SERVICE', 'PART']);

export const CreateServiceOrderBodySchema = z.object({
  quoteId: z.uuid(),
  organizationGroupId: z.uuid().nullable().optional(),
  warrantyOrigin: WarrantyOriginSchema.optional(),
});

export type CreateServiceOrderBody = z.infer<typeof CreateServiceOrderBodySchema>;

export const CreateServiceOrderResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  status: z.string(),
  itemCount: z.number().int().nonnegative(),
  correctsServiceOrderId: z.uuid().nullable(),
  isWarrantyClaim: z.boolean(),
  warrantyOrigin: WarrantyOriginSchema.nullable(),
});

export type CreateServiceOrderResponse = z.infer<typeof CreateServiceOrderResponseSchema>;

/** BR-SO-221 — open empty direct OS (vehicle chosen later on the OS). */
export const CreateServiceOrderFromReceptionBodySchema = z.object({
  placeId: z.uuid(),
  organizationGroupId: z.uuid().nullable().optional(),
});

export type CreateServiceOrderFromReceptionBody = z.infer<
  typeof CreateServiceOrderFromReceptionBodySchema
>;

export const CreateServiceOrderFromReceptionResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  status: z.string(),
  itemCount: z.number().int().nonnegative(),
});

export type CreateServiceOrderFromReceptionResponse = z.infer<
  typeof CreateServiceOrderFromReceptionResponseSchema
>;

/** BR-SO-221 — bind vehicle + active owner on a direct OS. */
export const SetServiceOrderVehicleBodySchema = z.object({
  customerVehicleId: z.uuid(),
});

export type SetServiceOrderVehicleBody = z.infer<typeof SetServiceOrderVehicleBodySchema>;

export const SetServiceOrderVehicleResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  customerVehicleId: z.uuid(),
  partyId: z.uuid(),
  customerRoleId: z.uuid(),
});

export type SetServiceOrderVehicleResponse = z.infer<typeof SetServiceOrderVehicleResponseSchema>;

export const SetServiceOrderWarrantyOriginBodySchema = z.object({
  warrantyOrigin: WarrantyOriginSchema,
});

export type SetServiceOrderWarrantyOriginBody = z.infer<
  typeof SetServiceOrderWarrantyOriginBodySchema
>;

export const SetServiceOrderWarrantyOriginResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  isWarrantyClaim: z.boolean(),
  warrantyOrigin: WarrantyOriginSchema,
});

export type SetServiceOrderWarrantyOriginResponse = z.infer<
  typeof SetServiceOrderWarrantyOriginResponseSchema
>;

export const SetServiceOrderCommissionRateBodySchema = z.object({
  mechanicRateBps: z.number().nullable().optional(),
  sellerRateBps: z.number().nullable().optional(),
});

export type SetServiceOrderCommissionRateBody = z.infer<
  typeof SetServiceOrderCommissionRateBodySchema
>;

export const SetServiceOrderCommissionRateResponseSchema = z.object({
  serviceOrderId: z.uuid(),
  mechanicCommissionRateBps: z.number().int().positive().nullable(),
  sellerCommissionRateBps: z.number().int().positive().nullable(),
});

export type SetServiceOrderCommissionRateResponse = z.infer<
  typeof SetServiceOrderCommissionRateResponseSchema
>;

export const ChangeServiceOrderStatusBodySchema = z.object({
  toStatus: z.enum([
    'IN_PROGRESS',
    'WAITING_PARTS',
    'AWAITING_APPROVAL',
    'AWAITING_EXTERNAL_PARTNER',
    'READY_FOR_DELIVERY',
    'CANCELLED',
  ]),
  reason: z.string().max(500).optional(),
});

export type ChangeServiceOrderStatusBody = z.infer<typeof ChangeServiceOrderStatusBodySchema>;

export const ServiceOrderItemResponseSchema = z.object({
  id: z.uuid(),
  itemType: z.string(),
  quantity: z.string(),
  unitPriceCents: z.string().nullable(),
  totalCents: z.string(),
  currency: z.string(),
  executionStatus: z.string(),
  approvedByCustomer: z.boolean(),
  rejectedByCustomer: z.boolean(),
  warrantyFlag: z.boolean(),
  warrantyUntil: z.string().nullable(),
});

export const ServiceOrderStatusHistoryResponseSchema = z.object({
  id: z.uuid(),
  fromStatus: z.string().nullable(),
  toStatus: z.string(),
  changedByUserId: z.uuid(),
  reason: z.string().nullable(),
  changedAt: z.string(),
});
